import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Filter,
  ArrowUpNarrowWide,
  ArrowDownWideNarrow,
  Link as LinkIcon,
  X,
} from "lucide-react";
import { listAllDropShip, getApprovedQtyShipDirect } from "../../../../reducers/shipdirect.slice";
import { createOrder } from "../../../../reducers/order.slice";
import { formatNumberToDollar, countActiveFiltersAndSort } from "../../../../utils/util";
import { AuthContext } from "../../../../context/authContext";
import { Button } from "../../../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "../../../../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../../../components/ui/dialog";
import PageHeader from "../../../../components/shared/PageHeader";
import SearchBar from "../../../../components/shared/SearchBar";
import RefreshButton from "../../../../components/shared/RefreshButton";
import ViewToggle from "../../../../components/shared/ViewToggle";
import FieldBlock from "../../../../components/shared/FieldBlock";
import CreateOrderAgreement from "../../../../components/shared/CreateOrderAgreement";
import useMediaQuery from "../../../../components/shared/useMediaQuery";

const STATUS_OPTIONS = ["ACTIVE", "INACTIVE"];
const FALLBACK_IMAGE = "https://cdn.pixabay.com/photo/2024/06/26/23/36/package-8856091_1280.png";
const ROWS_PER_PAGE_DEFAULT = 8;
const LIST_FACT_PROPS = { labelSize: 9.5, valueSize: 12, valueWeight: 700 };

const getRemainingQuantity = (item) =>
  Math.max(0, Number(item?.total_quantity || 0) - Number(item?.available_quantity || 0));

const handleImageFallback = (event) => {
  if (event.currentTarget.src !== FALLBACK_IMAGE) {
    event.currentTarget.src = FALLBACK_IMAGE;
  }
};

const MemberShipDirectPage = () => {
  const [selectedCards, setSelectedCards] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState([]);
  const [shipDirectDetails, setShipDirectDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState(() => {
    const storedStatuses = sessionStorage.getItem("shipDirectMember_selectedStatuses");
    return storedStatuses ? JSON.parse(storedStatuses) : [];
  });
  const [sortDirection, setSortDirection] = useState(() => {
    return sessionStorage.getItem("shipDirectMember_sortDirection") || null;
  });
  const [filterOpen, setFilterOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("shipDirectViewMode") || "card");
  const [openViewImgDialogue, setOpenViewImgDialogue] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [page, setPage] = useState(Number(sessionStorage.getItem("tablePage")) || 0);
  const [rowsPerPage] = useState(
    Number(sessionStorage.getItem("tableRowsPerPage")) || ROWS_PER_PAGE_DEFAULT
  );

  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [orderLines, setOrderLines] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [agreementChecked, setAgreementChecked] = useState(false);
  const [agreementError, setAgreementError] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showToast } = useContext(AuthContext);
  const isMobile = useMediaQuery("(max-width: 640px)");
  const isPhone = useMediaQuery("(max-width: 640px)");
  const isCompactList = useMediaQuery("(max-width: 1100px)");

  const handleViewChange = (newView) => {
    setViewMode(newView);
    localStorage.setItem("shipDirectViewMode", newView);
  };

  const getAllShipDirect = () => {
    setLoading(true);
    dispatch(listAllDropShip()).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setLoading(false);
        showToast(res?.error?.message, "error");
        return;
      }
      const formattedShipDirect = res?.payload?.map((item) => {
        const buyPrice = formatNumberToDollar(item?.buy_price);
        const cashoutPrice = formatNumberToDollar(item?.cashout_price);
        return { ...item, buyPrice, cashoutPrice };
      });
      setLoading(false);
      setShipDirectDetails(formattedShipDirect || []);
    });
  };

  useEffect(() => {
    getAllShipDirect();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setSearchResults(shipDirectDetails);
  }, [shipDirectDetails]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!debouncedSearch || !debouncedSearch.trim()) {
        setSearchResults(shipDirectDetails);
        return;
      }
      const query = debouncedSearch.toLowerCase();
      setSearchResults(
        shipDirectDetails.filter((row) =>
          ["upc", "retailer_name", "item_name"].some((param) => {
            const value = row[param];
            return value && value.toString().toLowerCase().includes(query);
          })
        )
      );
    }, 300);
    return () => clearTimeout(timer);
  }, [debouncedSearch, shipDirectDetails]);

  useEffect(() => {
    let filtered = [...searchResults];

    if (selectedStatuses?.length > 0) {
      filtered = filtered.filter((item) => selectedStatuses.includes(item?.status));
    }

    if (sortDirection === "asc" || sortDirection === "desc") {
      filtered.sort((a, b) => {
        const amountA = Number(a?.total_quantity || 0);
        const amountB = Number(b?.total_quantity || 0);
        return sortDirection === "asc" ? amountA - amountB : amountB - amountA;
      });
    }

    setFilteredData(filtered);
    setPage(0);
    sessionStorage.setItem("tablePage", 0);
  }, [searchResults, selectedStatuses, sortDirection]);

  useEffect(() => {
    sessionStorage.setItem("shipDirectMember_selectedStatuses", JSON.stringify(selectedStatuses));
  }, [selectedStatuses]);

  useEffect(() => {
    if (sortDirection) {
      sessionStorage.setItem("shipDirectMember_sortDirection", sortDirection);
    } else {
      sessionStorage.removeItem("shipDirectMember_sortDirection");
    }
  }, [sortDirection]);

  const handleStatusChange = (status, checked) => {
    setSelectedStatuses((prev) => (checked ? [...prev, status] : prev.filter((s) => s !== status)));
  };

  const handleSortAscending = () => setSortDirection((prev) => prev === "asc" ? null : "asc");
  const handleSortDescending = () => setSortDirection((prev) => prev === "desc" ? null : "desc");

  const handleChangePage = (newPage) => {
    setPage(newPage);
    sessionStorage.setItem("tablePage", newPage);
  };

  const toggleCardSelection = (card) => {
    if (getRemainingQuantity(card) === 0) return;
    setSelectedCards((prev) => {
      const isCardSelected = prev.some((selectedCard) => selectedCard.id === card.id);
      return isCardSelected
        ? prev.filter((selectedCard) => selectedCard.id !== card.id)
        : [...prev, card];
    });
  };

  const handleListSelectionToggle = (row) => {
    if (getRemainingQuantity(row) === 0) return;
    setSelectedOrder((prev) => {
      const isSelected = prev.some((selectedRow) => selectedRow.id === row.id);
      return isSelected ? prev.filter((selectedRow) => selectedRow.id !== row.id) : [...prev, row];
    });
  };

  const openImageDialog = (row) => {
    setSelectedRow(row);
    setOpenViewImgDialogue(true);
  };

  // Opens the "set quantities" dialog inline on this page instead of navigating
  // away — mirrors the artifact's catalog flow. Each line independently fetches
  // its real server-approved max quantity via getApprovedQtyShipDirect before the
  // stepper becomes usable, same validation the old confirm page enforced.
  const handleOpenOrderDialog = () => {
    const items = viewMode === "table" ? selectedOrder : selectedCards;
    if (!items.length) return;

    const lines = items.map((item) => ({
      id: item.id,
      item_name: item.item_name,
      item_link: item.item_link,
      upc: item.upc,
      retailer_name: item.retailer_name,
      buy_price: item.buy_price,
      cashout_price: item.cashout_price,
      cashoutPrice: item.cashoutPrice,
      quantity: 1,
      maxQty: null,
      loadingMax: true,
    }));
    setOrderLines(lines);
    setAgreementChecked(false);
    setAgreementError(false);
    setOrderDialogOpen(true);

    items.forEach((item) => {
      dispatch(getApprovedQtyShipDirect({ id: item.id })).then((res) => {
        const allowed = Number(res?.payload?.allowed_quantity) || 0;
        setOrderLines((prev) =>
          prev.map((line) =>
            line.id === item.id
              ? { ...line, maxQty: allowed, loadingMax: false, quantity: allowed > 0 ? Math.min(line.quantity, allowed) : 0 }
              : line
          )
        );
      });
    });
  };

  const handleQtyStep = (id, delta) => {
    setOrderLines((prev) =>
      prev.map((line) => {
        if (line.id !== id || line.loadingMax) return line;
        const max = line.maxQty || 0;
        return { ...line, quantity: Math.max(0, Math.min(max, line.quantity + delta)) };
      })
    );
  };

  const handleQtyInputChange = (id, rawValue) => {
    const digits = rawValue.replace(/\D/g, "");
    setOrderLines((prev) =>
      prev.map((line) => {
        if (line.id !== id || line.loadingMax) return line;
        const max = line.maxQty || 0;
        const parsed = digits === "" ? 0 : parseInt(digits, 10);
        return { ...line, quantity: Math.max(0, Math.min(max, parsed)) };
      })
    );
  };

  const handleRemoveLine = (id) => {
    setOrderLines((prev) => prev.filter((line) => line.id !== id));
    setSelectedCards((prev) => prev.filter((card) => card.id !== id));
    setSelectedOrder((prev) => prev.filter((row) => row.id !== id));
  };

  const handleOrderDialogChange = (open) => {
    setOrderDialogOpen(open);
    if (!open) {
      setOrderLines([]);
      setAgreementChecked(false);
    }
  };

  const handleConfirmCreateOrder = () => {
    if (!orderLines.length) return;
    if (!agreementChecked) {
      setAgreementError(true);
      showToast("Please agree to the order terms before creating the order.", "error");
      return;
    }
    const hasZeroQuantity = orderLines.some((line) => Number(line.quantity) === 0);
    if (hasZeroQuantity) {
      showToast("Quantity cannot be zero. Please update or remove all items.", "error");
      return;
    }

    setSubmitting(true);
    const order_items = orderLines.map((line) => ({
      item_name: line.item_name,
      item_link: line.item_link || "",
      upc: line.upc || "",
      quote_id: line.id || "",
      quantity: Number(line.quantity),
      buy_price: line.buy_price || 0,
      cashout_price: line.cashout_price || 0,
      total_amount: (line.cashout_price || 0) * Number(line.quantity),
      retailer_name: line.retailer_name || "",
    }));

    dispatch(createOrder({ type: "SHIP_DIRECT", order_items })).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setSubmitting(false);
        showToast(res?.error?.message, "error");
        return;
      }
      setSubmitting(false);
      setOrderDialogOpen(false);
      setOrderLines([]);
      setAgreementChecked(false);
      setSelectedCards([]);
      setSelectedOrder([]);
      showToast(
        `Order created with ${order_items.length} item${order_items.length > 1 ? "s" : ""} — pending approval in Open Orders`,
        "success"
      );
      navigate("/dashboard/open-orders");
    });
  };

  const renderProductCard = (item) => {
    const selected = selectedCards.some((card) => card.id === item.id);
    const disabled = getRemainingQuantity(item) === 0;

    return (
      <div
        key={item.id}
        style={{
          display: "flex",
          flexDirection: "column",
          position: "relative",
          borderRadius: 14,
          border: selected ? "1px solid #00ADB5" : "1px solid #2a2a2e",
          boxShadow: selected ? "0 0 0 1px #00ADB5 inset" : "none",
          background: "#1e1e1e",
          opacity: disabled ? 0.55 : 1,
          overflow: "hidden",
          transition: "border-color 0.15s",
        }}
      >
        <button
          type="button"
          aria-label="select item"
          disabled={disabled}
          onClick={() => toggleCardSelection(item)}
          style={{
            position: "absolute",
            top: 10,
            right: 10,
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 22,
            height: 22,
            borderRadius: 6,
            border: selected ? "2px solid #00ADB5" : "2px solid #2a2a2e",
            background: selected ? "#00ADB5" : "rgba(0,0,0,0.4)",
            cursor: disabled ? "not-allowed" : "pointer",
            color: "#00110f",
            fontSize: 13,
            fontWeight: 800,
          }}
        >
          {selected ? "✓" : ""}
        </button>

        <div
          onClick={() => openImageDialog(item)}
          style={{
            height: 130,
            background: "#fff",
            borderBottom: "1px solid #2a2a2e",
            cursor: "pointer",
          }}
        >
          <img
            src={item?.item_image_url || FALLBACK_IMAGE}
            alt={item?.item_name}
            onError={handleImageFallback}
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        </div>

        <div style={{ padding: 14 }}>
          <div
            title={item?.item_name}
            style={{
              color: "#fff",
              fontSize: 13.5,
              fontWeight: 700,
              lineHeight: 1.3,
              minHeight: 35,
              paddingRight: 20,
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflowWrap: "anywhere",
            }}
          >
            {item?.item_name}
          </div>
          <div
            style={{
              fontFamily: "ui-monospace, monospace",
              fontSize: 10,
              color: "#c3c8cc",
              margin: "3px 0 10px",
              overflowWrap: "anywhere",
            }}
          >
            ID: {item?.id}
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: "8px 12px",
              fontSize: 11,
              marginBottom: 10,
            }}
          >
            <FieldBlock label="UPC" value={item?.upc || "N/A"} wrap />
            <FieldBlock label="RETAILER" value={item?.retailer_name || "N/A"} wrap={isMobile} align="right" />
            <FieldBlock label="BUY PRICE" value={formatNumberToDollar(item?.buy_price || 0)} wrap={isMobile} />
            <FieldBlock label="CASHOUT" value={formatNumberToDollar(item?.cashout_price || 0)} color="#4caf50" wrap={isMobile} align="right" />
            <FieldBlock label="QUANTITY" value={`0/${getRemainingQuantity(item)}`} wrap={isMobile} />
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 8,
              paddingTop: 10,
              borderTop: "1px solid #2a2a2e",
            }}
          >
            <button
              type="button"
              onClick={() => {
                if (item?.item_link) window.open(item.item_link, "_blank");
                else showToast("Item link not available", "error");
              }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                background: "transparent",
                color: "#22d3ee",
                padding: 0,
                fontSize: 11,
                fontWeight: 700,
                cursor: "pointer",
                whiteSpace: "nowrap",
                border: "none",
              }}
            >
              <LinkIcon size={12} style={{ marginRight: 5 }} />
              View Product
            </button>
            <Button size="xs" disabled={disabled} onClick={() => toggleCardSelection(item)}>
              {selected ? "✓ Added" : "+ Add"}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const renderProductList = () => {
    if (isPhone) {
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {pagedRows.map((item) => {
            const selected = selectedOrder.some((row) => row.id === item.id);
            const disabled = getRemainingQuantity(item) === 0;
            return (
              <div
                key={item.id}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                  border: selected ? "1px solid rgba(0,173,181,0.36)" : "1px solid #24303a",
                  boxShadow: selected ? "0 0 0 1px rgba(0,173,181,0.18) inset" : "none",
                  borderRadius: 14,
                  background: "#1e1e1e",
                  padding: 10,
                  opacity: disabled ? 0.55 : 1,
                  minWidth: 0,
                }}
              >
                <div style={{ display: "grid", gridTemplateColumns: "64px minmax(0, 1fr)", gap: 10, alignItems: "start", minWidth: 0 }}>
                  <img
                    src={item?.item_image_url || FALLBACK_IMAGE}
                    alt={item?.item_name}
                    onError={handleImageFallback}
                    onClick={() => openImageDialog(item)}
                    style={{
                      width: 64,
                      height: 64,
                      objectFit: "contain",
                      borderRadius: 10,
                      border: "1px solid #25323c",
                      cursor: "pointer",
                      background: "#fff",
                    }}
                  />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ color: "#fff", fontWeight: 800, fontSize: 13.5, lineHeight: 1.3, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflowWrap: "anywhere" }}>
                      {item?.item_name}
                    </div>
                    <div style={{ color: "#c3c8cc", fontSize: 10, fontFamily: "ui-monospace, monospace", marginTop: 5, overflowWrap: "anywhere" }}>
                      ID: {item?.id}
                    </div>
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "8px 12px", minWidth: 0 }}>
                  <FieldBlock label="UPC" value={item?.upc || "N/A"} wrap />
                  <FieldBlock label="RETAILER" value={item?.retailer_name || "N/A"} wrap align="right" />
                  <FieldBlock label="BUY PRICE" value={formatNumberToDollar(item?.buy_price || 0)} wrap />
                  <FieldBlock label="CASHOUT" value={formatNumberToDollar(item?.cashout_price || 0)} color="#4caf50" wrap align="right" />
                  <FieldBlock label="QUANTITY" value={`0/${getRemainingQuantity(item)}`} wrap />
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, paddingTop: 10, borderTop: "1px solid #2a2a2e" }}>
                  <button
                    type="button"
                    onClick={() => {
                      if (item?.item_link) window.open(item.item_link, "_blank");
                      else showToast("Item link not available", "error");
                    }}
                    style={{ display: "inline-flex", alignItems: "center", background: "transparent", border: "none", color: "#22d3ee", padding: 0, fontSize: 11, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}
                  >
                    <LinkIcon size={12} style={{ marginRight: 5 }} />
                    View Product
                  </button>
                  <Button size="xs" disabled={disabled} onClick={() => handleListSelectionToggle(item)}>
                    {selected ? "✓ Added" : "+ Add"}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {pagedRows.map((item) => {
          const selected = selectedOrder.some((row) => row.id === item.id);
          const disabled = getRemainingQuantity(item) === 0;
          const detailGridColumns = isCompactList
            ? "repeat(auto-fit, minmax(96px, 1fr))"
            : "minmax(118px, 1.2fr) minmax(82px, 0.85fr) minmax(72px, 0.7fr) minmax(78px, 0.75fr) minmax(64px, 0.65fr)";

          return (
            <div
              key={item.id}
              style={{
                display: "grid",
                gridTemplateColumns: isCompactList
                  ? "72px minmax(0, 1fr) 116px"
                  : "72px minmax(250px, 0.85fr) minmax(0, 2.2fr) 124px",
                columnGap: isCompactList ? 12 : 18,
                rowGap: 12,
                alignItems: "center",
                border: selected ? "1px solid rgba(0,173,181,0.32)" : "1px solid #24303a",
                borderRadius: 14,
                background: "#1e1e1e",
                padding: isCompactList ? 12 : "14px 14px 14px 16px",
                opacity: disabled ? 0.55 : 1,
                minWidth: 0,
              }}
            >
              <img
                src={item?.item_image_url || FALLBACK_IMAGE}
                alt={item?.item_name}
                onError={handleImageFallback}
                onClick={() => openImageDialog(item)}
                style={{
                  width: 64,
                  height: 64,
                  objectFit: "contain",
                  borderRadius: 10,
                  border: "1px solid #25323c",
                  cursor: "pointer",
                  background: "#fff",
                }}
              />
              <div style={{ minWidth: 0 }}>
                <div style={{ color: "#fff", fontWeight: 800, fontSize: isCompactList ? 13.5 : 13, lineHeight: 1.3, overflowWrap: "anywhere" }}>
                  {item?.item_name}
                </div>
                <div style={{ color: "#c3c8cc", fontSize: 10, fontFamily: "ui-monospace, monospace", marginTop: 5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  ID: {item?.id}
                </div>
              </div>
              <div
                style={{
                  gridColumn: isCompactList ? "1 / -1" : undefined,
                  display: "grid",
                  gridTemplateColumns: detailGridColumns,
                  gap: isCompactList ? "8px 12px" : "8px 14px",
                  alignItems: "center",
                  minWidth: 0,
                }}
              >
                <FieldBlock label="UPC" value={item?.upc || "N/A"} wrap {...LIST_FACT_PROPS} />
                <FieldBlock label="RETAILER" value={item?.retailer_name || "N/A"} {...LIST_FACT_PROPS} />
                <FieldBlock label="BUY PRICE" value={formatNumberToDollar(item?.buy_price || 0)} {...LIST_FACT_PROPS} />
                <FieldBlock label="CASHOUT" value={formatNumberToDollar(item?.cashout_price || 0)} color="#fff" {...LIST_FACT_PROPS} />
                <FieldBlock label="QUANTITY" value={`0/${getRemainingQuantity(item)}`} {...LIST_FACT_PROPS} />
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "stretch", justifyContent: "center", gap: 10, minWidth: 0, width: "100%" }}>
                <Button
                  variant={selected ? "outline" : "default"}
                  size="sm"
                  disabled={disabled}
                  onClick={() => handleListSelectionToggle(item)}
                  style={{ width: "100%", height: 42, minWidth: 0, borderRadius: 10 }}
                >
                  {selected ? "Remove" : "Add"}
                </Button>
                <button
                  type="button"
                  onClick={() => {
                    if (item?.item_link) window.open(item.item_link, "_blank");
                    else showToast("Item link not available", "error");
                  }}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    minWidth: 0,
                    minHeight: 24,
                    border: "none",
                    background: "transparent",
                    color: "#9fb3c8",
                    padding: 0,
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  <LinkIcon size={13} style={{ marginRight: 5, color: "#8b9296" }} />
                  View Product
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const activeFilterCount = countActiveFiltersAndSort(sortDirection, selectedStatuses);
  const pagedRows = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const selectedCount = (viewMode === "table" ? selectedOrder : selectedCards).length;
  const orderTotal = orderLines.reduce((sum, line) => sum + (line.cashout_price || 0) * (line.quantity || 0), 0);

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <Dialog open={openViewImgDialogue} onOpenChange={setOpenViewImgDialogue}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedRow?.item_name || "Item image"}</DialogTitle>
            {selectedRow?.description && <DialogDescription>{selectedRow.description}</DialogDescription>}
          </DialogHeader>
          <div
            style={{
              width: "100%",
              height: isMobile ? 240 : 320,
              borderRadius: 10,
              overflow: "hidden",
              border: "1px solid rgba(255,255,255,0.08)",
              background: "#fff",
            }}
          >
            <img
              src={selectedRow?.item_image_url || FALLBACK_IMAGE}
              alt={selectedRow?.item_name}
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={orderDialogOpen} onOpenChange={handleOrderDialogChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create order — set quantities</DialogTitle>
            <DialogDescription>
              Confirm how many units of each item you're sending, then create your order.
            </DialogDescription>
          </DialogHeader>

          <div style={{ maxHeight: 340, overflowY: "auto" }}>
            {orderLines.map((line) => (
              <div
                key={line.id}
                style={{
                  display: "flex",
                  alignItems: isMobile ? "stretch" : "center",
                  justifyContent: "space-between",
                  gap: 12,
                  flexDirection: isMobile ? "column" : "row",
                  padding: "11px 0",
                  borderBottom: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                <div style={{ minWidth: 0, marginRight: 12 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: "#fff",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {line.item_name}
                  </div>
                  <div style={{ fontSize: 11, color: "#c3c8cc", marginTop: 2 }}>
                    @ {line.cashoutPrice} cashout ·{" "}
                    {line.loadingMax
                      ? "checking allowed qty…"
                      : line.maxQty > 0
                        ? `${line.maxQty} available`
                        : "not available right now"}
                  </div>
                </div>

                <div style={{ display: "flex", alignItems: "center", flexShrink: 0, justifyContent: isMobile ? "space-between" : "flex-start", width: isMobile ? "100%" : "auto" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      border: "1px solid #2a2a2e",
                      borderRadius: 8,
                      overflow: "hidden",
                    }}
                  >
                    <button
                      type="button"
                      aria-label="decrease quantity"
                      disabled={line.loadingMax || line.quantity <= 0}
                      onClick={() => handleQtyStep(line.id, -1)}
                      style={{
                        width: 28,
                        height: 28,
                        background: "#1e1e1e",
                        border: "none",
                        color: line.loadingMax || line.quantity <= 0 ? "#4a4a4e" : "#fff",
                        fontSize: 15,
                        cursor: line.loadingMax || line.quantity <= 0 ? "not-allowed" : "pointer",
                      }}
                    >
                      −
                    </button>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={line.quantity}
                      disabled={line.loadingMax}
                      onChange={(e) => handleQtyInputChange(line.id, e.target.value)}
                      style={{
                        width: 44,
                        height: 28,
                        textAlign: "center",
                        background: "#000",
                        border: "none",
                        borderLeft: "1px solid #2a2a2e",
                        borderRight: "1px solid #2a2a2e",
                        color: "#fff",
                        fontFamily: "ui-monospace, monospace",
                        fontSize: 13,
                      }}
                    />
                    <button
                      type="button"
                      aria-label="increase quantity"
                      disabled={line.loadingMax || line.quantity >= (line.maxQty || 0)}
                      onClick={() => handleQtyStep(line.id, 1)}
                      style={{
                        width: 28,
                        height: 28,
                        background: "#1e1e1e",
                        border: "none",
                        color: line.loadingMax || line.quantity >= (line.maxQty || 0) ? "#4a4a4e" : "#fff",
                        fontSize: 15,
                        cursor: line.loadingMax || line.quantity >= (line.maxQty || 0) ? "not-allowed" : "pointer",
                      }}
                    >
                      +
                    </button>
                  </div>

                  <button
                    type="button"
                    aria-label="remove from order"
                    onClick={() => handleRemoveLine(line.id)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 26,
                      height: 26,
                      marginLeft: 8,
                      borderRadius: 7,
                      background: "transparent",
                      border: "none",
                      color: "#c3c8cc",
                      cursor: "pointer",
                    }}
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div
            style={{
              background: "#1e1e1e",
              borderRadius: 10,
              padding: "12px 14px",
              fontSize: 13,
              display: "flex",
              justifyContent: "space-between",
              marginTop: 14,
            }}
          >
            <span style={{ color: "#c3c8cc" }}>Estimated cashout</span>
            <b style={{ fontFamily: "ui-monospace, monospace", color: "#fff" }}>
              {formatNumberToDollar(orderTotal)}
            </b>
          </div>

          <CreateOrderAgreement
            checked={agreementChecked}
            onChange={(value) => {
              setAgreementChecked(value);
              setAgreementError(false);
            }}
            showPriceLock={false}
            error={agreementError}
          />

          <DialogFooter>
            <Button variant="outline" onClick={() => setOrderDialogOpen(false)}>
              Back
            </Button>
            <div style={{ marginLeft: 10 }}>
              <Button
                onClick={handleConfirmCreateOrder}
                disabled={submitting || orderLines.length === 0 || orderLines.some((l) => l.loadingMax)}
              >
                {submitting ? "Creating…" : "Create order →"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div style={{ marginBottom: 20 }}>
        <PageHeader
          title="Ship Directs"
          subtitle="Use Ship Direct when you're shipping from a retailer or shipping yourself, then upload tracking from your pending shipment."
        />
      </div>

      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          flexWrap: "wrap",
          marginBottom: 20,
          minWidth: 0,
        }}
      >
        <div style={{ flex: "1 1 420px", minWidth: isPhone ? "100%" : 280 }}>
          <SearchBar
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            setDebouncedTerm={setDebouncedSearch}
            fullWidth
            height={42}
          />
        </div>

        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            flexWrap: "wrap",
            justifyContent: isPhone ? "stretch" : "flex-end",
            flex: isPhone ? "1 1 100%" : "0 0 auto",
            width: isPhone ? "100%" : undefined,
            minWidth: 0,
          }}
        >
          {!isMobile && <RefreshButton onRefresh={getAllShipDirect} />}

          <ViewToggle viewMode={viewMode} onChange={handleViewChange} height={42} buttonSize={34} />

          <DropdownMenu open={filterOpen} onOpenChange={setFilterOpen}>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" style={{ height: 42, flex: isPhone ? "1 1 auto" : "0 0 auto" }}>
                <Filter size={13} style={{ marginRight: 6 }} />
                Filter & Sort
                {activeFilterCount > 0 && (
                  <span
                    style={{
                      marginLeft: 6,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: 16,
                      height: 16,
                      borderRadius: 8,
                      background: "#00ADB5",
                      color: "#000",
                      fontSize: 10,
                      fontWeight: 800,
                      padding: "0 4px",
                    }}
                  >
                    {activeFilterCount}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="tw-w-64">
              <DropdownMenuLabel>Filter by Status</DropdownMenuLabel>
              {STATUS_OPTIONS.map((status) => (
                <DropdownMenuCheckboxItem
                  key={status}
                  checked={selectedStatuses.includes(status)}
                  onSelect={(e) => e.preventDefault()}
                  onCheckedChange={(checked) => handleStatusChange(status, checked)}
                >
                  {status}
                </DropdownMenuCheckboxItem>
              ))}

              <DropdownMenuSeparator />

              <DropdownMenuItem active={sortDirection === "asc"} onClick={handleSortAscending}>
                <ArrowUpNarrowWide size={14} style={{ marginRight: 8 }} />
                Sort by Quantity (Low to High)
              </DropdownMenuItem>
              <DropdownMenuItem active={sortDirection === "desc"} onClick={handleSortDescending}>
                <ArrowDownWideNarrow size={14} style={{ marginRight: 8 }} />
                Sort by Quantity (High to Low)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            onClick={handleOpenOrderDialog}
            disabled={selectedCount === 0}
            style={{
              height: 42,
              width: isPhone ? "100%" : undefined,
              minWidth: isPhone ? 0 : 146,
              flex: isPhone ? "1 1 100%" : "0 0 auto",
              borderRadius: 10,
              fontWeight: 800,
              boxShadow: selectedCount > 0 ? "0 10px 24px -18px rgba(0,173,181,0.85)" : "none",
            }}
          >
            Create Order{selectedCount > 0 ? ` (${selectedCount})` : ""}
          </Button>
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column" }}>
        {pagedRows.length > 0 ? (
          viewMode === "table" ? (
            renderProductList()
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isPhone ? "1fr" : `repeat(auto-fill, minmax(${isMobile ? "210px" : "246px"}, 1fr))`,
                gap: isPhone ? 12 : 16,
              }}
            >
              {pagedRows.map(renderProductCard)}
            </div>
          )
        ) : (
          <div
            style={{
              height: 200,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px dashed rgba(255,255,255,0.12)",
              borderRadius: 14,
              color: "#c3c8cc",
              fontSize: 13,
            }}
          >
            {loading ? "Loading…" : "No data found"}
          </div>
        )}

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: isPhone ? "space-between" : "flex-end",
            gap: 10,
            marginTop: 16,
            flexWrap: "wrap",
          }}
        >
          <span style={{ color: "#9fb3c8", fontSize: 12 }}>
            {filteredData.length === 0 ? 0 : page * rowsPerPage + 1}-
            {Math.min((page + 1) * rowsPerPage, filteredData.length)} of {filteredData.length}
          </span>
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => handleChangePage(Math.max(0, page - 1))}>
            Prev
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={(page + 1) * rowsPerPage >= filteredData.length}
            onClick={() => handleChangePage(page + 1)}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MemberShipDirectPage;
